import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface DayObject {
  id: string;
  date?: string;
  startTime?: string;
  startListFile?: string | null;
}

export interface CompetitionObject {
  id: string;
  name: string;
  locationName: string;
  days: DayObject[];
  resultAttachments: string[];
  invitationFile?: string | null;
  logo?: string | null;
  status: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CompetitionListInfo {
  competitions: CompetitionObject[];
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  errors: unknown;
}

@Module
export default class CompetitionModule
  extends VuexModule
  implements CompetitionListInfo
{
  competitions: CompetitionObject[] = [];

  page = 1;
  take = 10;
  itemCount = 0;
  pageCount = 1;
  hasPreviousPage = false;
  hasNextPage = true;
  errors = {};

  /**
   * Get all competitions
   */
  get allCompetitions(): CompetitionObject[] {
    return this.competitions;
  }

  @Mutation
  [Mutations.SET_COMPETITIONS](data) {
    this.competitions = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_COMPETITION_CURRENT_PAGE](page: number) {
    this.page = page;
  }

  @Mutation
  [Mutations.RESET_COMPETITIONS]() {
    this.competitions = [];
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Action
  [Actions.UPDATE_COMPETITION_CURRENT_PAGE](page: number) {
    this.context.commit(Mutations.SET_COMPETITION_CURRENT_PAGE, page);
  }

  @Action
  [Actions.GET_COMPETITION_LIST]() {
    ApiService.setAuthorizationHeader();
    const params = { page: this.page, take: this.take };
    return ApiService.query('/competition', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_COMPETITIONS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_COMPETITION](id: string) {
    ApiService.setAuthorizationHeader();
    return ApiService.get(`/competition/${id}`);
  }

  @Action
  [Actions.CREATE_COMPETITION](data: any) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/competition', data)
      .then((res) => {
        this.context.commit(Mutations.RESET_COMPETITIONS);
        return res.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_COMPETITION](payload: any) {
    const { id, ...data } = payload;
    ApiService.setAuthorizationHeader();
    console.log(data, 'data');

    return ApiService.update(`/competition`, id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_COMPETITIONS);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPLOAD_COMPETITION_LOGO]({
    competitionId,
    file,
  }: {
    competitionId: string;
    file: File | File[];
  }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();

    const fd = new FormData();
    const filesArray = Array.isArray(file) ? file : [file];
    filesArray.forEach((f) => fd.append('logo', f));

    return ApiService.vueInstance.axios
      .post(`/competition/logo/${competitionId}`, fd)
      .then(() => this.context.commit(Mutations.RESET_COMPETITIONS))
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPLOAD_COMPETITION_INVITATION]({
    competitionId,
    file,
  }: {
    competitionId: string;
    file: File | File[];
  }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();

    const fd = new FormData();
    const filesArray = Array.isArray(file) ? file : [file];
    filesArray.forEach((f) => fd.append('invitation', f));

    return ApiService.vueInstance.axios
      .post(`/competition/invitation/${competitionId}`, fd)
      .then(() => this.context.commit(Mutations.RESET_COMPETITIONS))
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPLOAD_COMPETITION_RESULTS]({
    competitionId,
    files,
  }: {
    competitionId: string;
    files: File | File[];
  }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();

    const fd = new FormData();
    const filesArray = Array.isArray(files) ? files : [files];
    filesArray.forEach((f) => fd.append('results', f));

    return ApiService.vueInstance.axios
      .post(`/competition/result/${competitionId}`, fd)
      .then(() => this.context.commit(Mutations.RESET_COMPETITIONS))
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPLOAD_COMPETITION_DAY_FILE]({
    competitionId,
    dayId,
    file,
  }: {
    competitionId: string;
    dayId: string;
    file: File | File[];
  }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();

    const fd = new FormData();
    const filesArray = Array.isArray(file) ? file : [file];

    filesArray.forEach((f) => fd.append('startList', f));

    return ApiService.vueInstance.axios
      .post(`/competition/start-list/${competitionId}/${dayId}`, fd)
      .then(() => this.context.commit(Mutations.RESET_COMPETITIONS))
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.DELETE_COMPETITION](id: string) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete(`/competition`, id)
      .then(() => {
        this.context.commit(Mutations.RESET_COMPETITIONS);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }
}
